'use strict';

import Order from 'common/models/Order'

let cache = {};

const ORDERS_UPDATE_FETCH_API = '/api/orders/';
const ORDERS_CREATE_API = '/api/orders.json';
const PAYMENT_API = '/api/payment_records/';
const NEW_ORDER_ID = 'new';

const UNSUPPORTED_ORDER_VERSION = 'UNSUPPORTED_ORDER_VERSION';
const NO_QUOTES_FOR_ORDER = 'NO_QUOTES_FOR_ORDER';
const GO_FIGURE = 'GO_FIGURE';
const FINALIZED_ORDER_EDIT = 'FINALIZED_ORDER_EDIT';
const EXPIRED_ORDER = 'EXPIRED_ORDER';
const NO_ORDER_FOUND = 'NO_ORDER_FOUND';
const ORDER_NOT_QUOTED = 'ORDER_NOT_QUOTED';
const ORDER_IN_DRAFTED = 'ORDER_IN_DRAFTED';
const ORDER_IN_QUOTED = 'ORDER_IN_QUOTED';
const NO_BILL_FOUND = 'NO_BILL_FOUND';
const ORDER_IN_CONFIRMED = 'ORDER_IN_CONFIRMED';

class OrderStore {
    /*@ngInject*/
    constructor(inventory, $q, $http, auth, $state, utils) {
        "ngInject";
        _.extend(this, {
            inventory: inventory,
            $q: $q,
            $http: $http,
            auth: auth,
            $state: $state,
            utils: utils
        });
    }

    getOrderWithBillWithRedirectionCheck(orderId) {
        return this.getOrderWithQuote(orderId)
            .then(
                (orderWithBill) => {
                    let order = orderWithBill.order;
                    if(order.status && (order.status === this.status.FINALIZED || order.status === this.status.CLOSED || order.status === this.status.LOST || order.status === this.status.ASSIGNED || order.status === this.status.INTRANSIT || order.status === this.status.DELIVERED || order.status === this.status.INSTORAGE || order.status === this.status.PICKUPCOMPLETED)) {
                        return this.$q.reject({
                            error: {
                                code: FINALIZED_ORDER_EDIT,
                                entityType: 'order',
                                entityId: order.id,
                                response: order
                            }
                        });
                    }
                    if(order.order_time < this.utils.getCurrentDateInSeconds()) {
                        return this.$q.reject({
                            error: {
                                code: EXPIRED_ORDER,
                                entityType: 'order',
                                entityId: order.id,
                                response: order
                            }
                        });
                    }
                    return orderWithBill;
                }
            );
    }

    getOrderWithQuoteWithRedirectionCheck(orderId) {
        return this.getOrderWithQuote(orderId)
            .then(
                (orderWithQuote) => {
                    let order = orderWithQuote.order;
                    if(order.status && (order.status === this.status.FINALIZED || order.status === this.status.CLOSED || order.status === this.status.LOST || order.status === this.status.ASSIGNED || order.status === this.status.INTRANSIT || order.status === this.status.DELIVERED || order.status === this.status.INSTORAGE || order.status === this.status.PICKUPCOMPLETED)) {
                        return this.$q.reject({
                            error: {
                                code: FINALIZED_ORDER_EDIT,
                                entityType: 'order',
                                entityId: order.id,
                                response: order
                            }
                        });
                    }
                    if(order.order_time < this.utils.getCurrentDateInSeconds()) {
                        return this.$q.reject({
                            error: {
                                code: EXPIRED_ORDER,
                                entityType: 'order',
                                entityId: order.id,
                                response: order
                            }
                        });
                    }
                    return orderWithQuote;
                }
            );
    }

    getOrderWithRedirectionCheck(orderId, updateOrderItems) {
        return this.getOrder(orderId, updateOrderItems)
            .then(
                (order) => {
                    if(order.status && (order.status === this.status.FINALIZED || order.status === this.status.CLOSED || order.status === this.status.LOST || order.status === this.status.ASSIGNED || order.status === this.status.INTRANSIT || order.status === this.status.DELIVERED || order.status === this.status.INSTORAGE || order.status === this.status.PICKUPCOMPLETED)) {
                        return this.$q.reject({
                            error: {
                                code: FINALIZED_ORDER_EDIT,
                                entityType: 'order',
                                entityId: order.id,
                                response: order
                            }
                        });
                    }
                    if(order.order_time < this.utils.getCurrentDateInSeconds()) {
                        return this.$q.reject({
                            error: {
                                code: EXPIRED_ORDER,
                                entityType: 'order',
                                entityId: order.id,
                                response: order
                            }
                        });
                    }
                    return order;
                }
            );
    }

    getOrder(orderId, updateOrderItems) {
        let authenticatedUser = this.auth.currentUser() || {};

        /**
         * If this is a new order,
         * we do not care about auth at this stage.
         */
        if (orderId === NEW_ORDER_ID) {
            if (!cache[NEW_ORDER_ID]) {
                cache[NEW_ORDER_ID] = this.getNewOrder();
            }

            if (cache[NEW_ORDER_ID].property_type && updateOrderItems) {
                cache[NEW_ORDER_ID].order_items = this.inventory.fetchDefaultItems(cache[NEW_ORDER_ID]);
            }

            return this.$q.when(cache[NEW_ORDER_ID]);
        }

        /**
         * If the user is trying to access an existing order,
         *  and is not authenticated yet,
         *  proceed after auth using login modal
         */
        if (orderId !== NEW_ORDER_ID && !this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
            return this.auth.loginUsingTpinModal()
                .then(
                    () => this.getOrder(orderId),
                    () => this.$q.reject({auth: false})
                );
        } else if (orderId !== NEW_ORDER_ID && (this.auth.isAuthenticated() || this.auth.isAdminUser())) {
            const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API, {
                id: orderId,
                phone_number: authenticatedUser.phone_number,
                user_token: authenticatedUser.authentication_token
            });
            return this.fetch(constructedAPI, 'order', orderId);
        }

        return this.$q.reject(false);
    }

    getQuoteForOrder(orderId) {
        let authenticatedUser = this.auth.currentUser() || {};

        /**
         * If the user is trying to access an existing order,
         *  and is not authenticated yet,
         *  proceed after auth using login modal
         */
        if (orderId !== NEW_ORDER_ID && !this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
            return this.auth.loginUsingTpinModal()
                .then(
                    () => this.getQuoteForOrder(orderId),
                    () => this.$q.reject({auth: false})
                );
        } else if (orderId !== NEW_ORDER_ID && (this.auth.isAuthenticated() || this.auth.isAdminUser())) {
            const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API + orderId + "/fetch_quotes.json", {
                phone_number: authenticatedUser.phone_number,
                user_token: authenticatedUser.authentication_token
            });
            return this.fetch(constructedAPI, 'quote', orderId);
        }

        return this.$q.reject(false);
    }

    getPaymentForOrder(orderId, couponCode) {
        let authenticatedUser = this.auth.currentUser() || {};

        /**
         * If the user is trying to access an existing order,
         *  and is not authenticated yet,
         *  proceed after auth using login modal
         */
        if (orderId !== NEW_ORDER_ID && !this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
            return this.auth.loginUsingTpinModal()
                .then(
                    () => this.getPaymentForOrder(orderId, couponCode),
                    () => this.$q.reject({auth: false})
                );
        } else if (orderId !== NEW_ORDER_ID && (this.auth.isAuthenticated() || this.auth.isAdminUser())) {
            const constructedAPI = this.constructAPIWith(PAYMENT_API + 'create_payment_for_order.json', {});
            return this.$http({
                method: 'POST',
                url: constructedAPI,
                headers: "application/json; charset=utf-8",
                data: {
                    phone_number: authenticatedUser.phone_number,
                    user_token: authenticatedUser.authentication_token,
                    order_id: orderId,
                    coupon_code: couponCode
                }
            })
        }

        return this.$q.reject(false);
    }

    getTokenPaymentForOrder(orderId) {
        let authenticatedUser = this.auth.currentUser() || {};

        /**
         * If the user is trying to access an existing order,
         *  and is not authenticated yet,
         *  proceed after auth using login modal
         */
        if (orderId !== NEW_ORDER_ID && !this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
            return this.auth.loginUsingTpinModal()
                .then(
                () => this.getTokenPaymentForOrder(orderId),
                () => this.$q.reject({auth: false})
            );
        } else if (orderId !== NEW_ORDER_ID && (this.auth.isAuthenticated() || this.auth.isAdminUser())) {
            const constructedAPI = this.constructAPIWith(PAYMENT_API + 'create_token_payment_for_order.json', {});
            return this.$http({
                method: 'POST',
                url: constructedAPI,
                headers: "application/json; charset=utf-8",
                data: {
                    phone_number: authenticatedUser.phone_number,
                    user_token: authenticatedUser.authentication_token,
                    order_id: orderId
                }
            })
        }

        return this.$q.reject(false);
    }

    getPaymentStatusForOrder(orderId) {
        let authenticatedUser = this.auth.currentUser() || {};

        /**
         * If the user is trying to access an existing order,
         *  and is not authenticated yet,
         *  proceed after auth using login modal
         */
        if (orderId !== NEW_ORDER_ID && !this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
            return this.auth.loginUsingTpinModal()
                .then(
                    () => this.getPaymentStatusForOrder(orderId),
                    () => this.$q.reject({auth: false})
                );
        } else if (orderId !== NEW_ORDER_ID && (this.auth.isAuthenticated() || this.auth.isAdminUser())) {
            const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API + orderId + "/payment_status.json", {
                user_token: authenticatedUser.authentication_token,
                phone_number: authenticatedUser.phone_number
            });
            return this.fetch(constructedAPI);
        }

        return this.$q.reject(false);
    }

    sendTpinForOrder(orderId) {
        const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API + "/verify_user.json", {
            orderId: orderId
        });
        return this.fetch(constructedAPI);
    }

    getUserForOrder(orderId, tpin) {
        const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API + "/authenticate_user_for_order", {
            orderId: orderId,
            tpin: tpin
        });
        return this.fetch(constructedAPI);
    }

    getBillForOrder(orderId, couponCode) {
        let authenticatedUser = this.auth.currentUser() || {};

        /**
         * If the user is trying to access an existing order,
         *  and is not authenticated yet,
         *  proceed after auth using login modal
         */
        if (orderId !== NEW_ORDER_ID && !this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
            return this.auth.loginUsingTpinModal()
                .then(
                    () => this.getBillForOrder(orderId),
                    () => this.$q.reject({auth: false})
                );
        } else if (orderId !== NEW_ORDER_ID && (this.auth.isAuthenticated() || this.auth.isAdminUser())) {
            const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API + orderId + "/get_bill.json", {
                user_token: authenticatedUser.authentication_token,
                phone_number: authenticatedUser.phone_number,
                coupon_code: couponCode
            });
            return this.fetch(constructedAPI, 'bill', orderId);
        }

        return this.$q.reject(false);
    }

    getPaymentRecord(orderId, paymentRecordId) {
        let authenticatedUser = this.auth.currentUser() || {};

        if (orderId !== NEW_ORDER_ID && !this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
            return this.auth.loginUsingTpinModal()
                .then(
                    () => this.getBillForOrder(orderId),
                    () => this.$q.reject({auth: false})
                );
        } else if (orderId !== NEW_ORDER_ID && (this.auth.isAuthenticated() || this.auth.isAdminUser())) {
            const constructedAPI = this.constructAPIWith(PAYMENT_API + "get_payment_record.json", {
                user_token: authenticatedUser.authentication_token,
                phone_number: authenticatedUser.phone_number,
                payment_record_id: paymentRecordId
            });
            return this.fetch(constructedAPI, 'payment_record', paymentRecordId);
        }
    }

    updateDamageCover(orderId, damageCoverStatus) {
        let authenticatedUser = this.auth.currentUser() || {};
        /**
         * If the user is trying to access an existing order,
         *  and is not authenticated yet,
         *  proceed after auth using login modal
         */
        if (orderId !== NEW_ORDER_ID && !this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
            return this.auth.loginUsingTpinModal()
                .then(
                () => this.updateDamageCover(orderId, couponCode),
                () => this.$q.reject({auth: false})
            );
        } else if (orderId !== NEW_ORDER_ID && (this.auth.isAuthenticated() || this.auth.isAdminUser())) {
            const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API + orderId + "/toggle_damage_cover_payable.json", { });
            return this.$http({
                method: 'POST',
                url: constructedAPI,
                headers: "application/json; charset=utf-8",
                data: {
                    user_id: authenticatedUser.id,
                    user_token: authenticatedUser.authentication_token,
                    phone_number: authenticatedUser.phone_number,
                    damage_cover_status: damageCoverStatus

                }
            })
        }
    }

    getDiscountForOrder(orderId, couponCode) {
        let authenticatedUser = this.auth.currentUser() || {};

        /**
         * If the user is trying to access an existing order,
         *  and is not authenticated yet,
         *  proceed after auth using login modal
         */
        if (orderId !== NEW_ORDER_ID && !this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
            return this.auth.loginUsingTpinModal()
                .then(
                    () => this.getDiscountForOrder(orderId, couponCode),
                    () => this.$q.reject({auth: false})
                );
        } else if (orderId !== NEW_ORDER_ID && (this.auth.isAuthenticated() || this.auth.isAdminUser())) {
            const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API + orderId + "/discount_amount.json", {
                user_token: authenticatedUser.authentication_token,
                phone_number: authenticatedUser.phone_number,
                coupon_code: couponCode
            });
            return this.fetch(constructedAPI);
        }

        return this.$q.reject(false);
    }

    getOrderWithQuote(orderId) {
        return this.$q.all([this.getOrder(orderId), this.getQuoteForOrder(orderId)])
            .then((resolved) => ({order: resolved[0], quote: resolved[1]}));
    }

    getOrderWithBill(orderId) {
        return this.$q.all([this.getOrder(orderId), this.getBillForOrder(orderId)])
            .then((resolved) => ({order: resolved[0], bill: resolved[1]}));
    }

    getAllOrdersOfUser() {
        let authenticatedUser = this.auth.currentUser() || {};

        if (this.auth.isAuthenticated()) {
            const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API + "/user_orders.json", {
                user_token: authenticatedUser.authentication_token,
                phone_number: authenticatedUser.phone_number
            });
            return this.fetchAll(constructedAPI);
        }

        return this.$q.reject(false);
    }

    createOrder(order) {
        let authenticatedUser = this.auth.currentUser(),
            userId;

        if (authenticatedUser && authenticatedUser.authentication_token) {
            userId = authenticatedUser.id;
        }

        if (!userId && !order.user_id) {
            return this.$q.reject({auth: false});
        }

        return this.$http({
                method: 'POST',
                url: ORDERS_CREATE_API,
                headers: "application/json; charset=utf-8",
                data: {
                    order: _.extend({user_id: userId}, order)
                }
            }).then(
                (response) => {
                    let order = response.data;
                    if(cache[NEW_ORDER_ID]) {
                        delete cache[NEW_ORDER_ID];
                    }
                    cache[order.id] = this.rubifyOrder(order);
                    return cache[order.id];
                },
                (error) => {
                    console.log(error);
                }
            );
    }

    saveOrder(order, skipEmptyOrderItemsCheck) {
        if (order.id === NEW_ORDER_ID && this.auth.isAuthenticated() && this.canSaveOrder(order, skipEmptyOrderItemsCheck)) {
            return this.createOrder(order);
        }

        if (order.id === NEW_ORDER_ID && this.auth.isAdminUser() && order.user_id && this.canSaveOrder(order, skipEmptyOrderItemsCheck)) {
            return this.createOrder(order);
        }

        if (order.id !== NEW_ORDER_ID) {
            let authenticatedUser = this.auth.currentUser() || {};

            if (!this.auth.isAuthenticated() && !this.auth.isAdminUser()) {
                return this.auth.loginUsingTpinModal()
                    .then(
                        () => this.saveOrder(order),
                        () => this.$q.reject({auth: false})
                    );
            } else {
                const constructedAPI = this.constructAPIWith(ORDERS_UPDATE_FETCH_API, {
                    id: order.id
                });
                return this.$http({
                    method: 'PUT',
                    url: constructedAPI,
                    headers: "application/json; charset=utf-8",
                    data: {
                        user_id: authenticatedUser.id,
                        user_token: authenticatedUser.authentication_token,
                        phone_number: authenticatedUser.phone_number,
                        order: order
                    }
                }).then(
                    (response) => {
                        let order = response.data;
                        cache[order.id] = this.rubifyOrder(order);
                        return cache[order.id];
                    },
                    (error) => {
                        console.log(error);
                    }
                )
            }

        }

        cache[order.id] = order;
        return this.$q.when(cache[order.id]);
    }

    fetch(api, entityType, entityId) {
        let deferred = this.$q.defer();

        this.$http({
            method: 'GET',
            url: api,
            headers: "application/json; charset=utf-8"
        }).then(
            (response) => {
                let data = response.data;

                // This is a hack to determine orders from the older
                // version of the app
                // On such orders, fail the deferred with an appropriate message
                if (entityType === 'order' && _.isUndefined(data.move_type)) {
                    return deferred.reject({
                        error: {
                            code: UNSUPPORTED_ORDER_VERSION,
                            entityType: entityType,
                            entityId: entityId,
                            response: response
                        }
                    });
                }

                cache[api] = data;

                if (entityType === 'order') {
                    return deferred.resolve(this.rubifyOrder(cache[api]));
                }

                return deferred.resolve(cache[api]);
            },
            (error) => {
                return deferred.reject({
                    error: {
                        code: error.data.error_code || GO_FIGURE,
                        entityType: entityType,
                        entityId: entityId,
                        response: error
                    }
                });
            }
        );

        return deferred.promise;
    }

    fetchAll(api) {
        return this.$http({
            method: 'GET',
            url: api,
            headers: "application/json; charset=utf-8"
        }).then((response) => {
            let data = response.data;
            return _.map(data, (order) => this.rubifyOrder(order));
        });
    }

    constructAPIWith(root, parameters) {
        const apiRoot = parameters.id ? [root, parameters.id, ".json"].join('') : root;
        const urlParams = _(parameters)
            .pairs()
            .filter((pair) => pair[0] !== 'id')
            .map((pair) => pair.join('='))
            .join('&');

        return apiRoot + "?" + urlParams;
    }

    /**
     * An order can be saved if
     *  1. order has order items
     *  2. order has from and to addresses
     *  3. order is skipped
     *  4. order is drafted
     *  5. order is fully detailed
     * @param order
     */
    canSaveOrder(order, skipEmptyOrderItemsCheck) {
        const orderItems = order.order_items;
        if (_.isUndefined(orderItems)) {
            return false;
        }

        if(_.isEmpty(orderItems) && !skipEmptyOrderItemsCheck) {
            return false;
        }

        const fromAddress = order.from_address,
            toAddress = order.to_address;
        const fromAddressLat = fromAddress.latitude,
            fromAddressLon = fromAddress.longitude,
            toAddressLat = toAddress.latitude,
            toAddressLon = toAddress.longitude;
        if (!fromAddressLat || !fromAddressLon || !toAddressLat || !toAddressLon) {
            return false;
        }

        const orderIsSkipped = order.is_skipped === true;
        if (orderIsSkipped) {
            return true;
        }

        const orderIsInDraftState = order.status === this.status.DRAFTED;
        if (orderIsInDraftState) {
            return true;
        }

        const orderItemDetails = _.flatten(_.map(orderItems, (item) => _.values(item.order_item_details))),
            vehicleOrderDetails = _.flatten(_.map(orderItems, (item) => _.values(item.vehicle_order_details)));
        const orderItemsQty = _.sum(orderItems, 'quantity'),
            orderItemDetailsQty = _.sum(orderItemDetails, 'quantity'),
            vehicleOrderDetailsQty = _.sum(vehicleOrderDetails, 'quantity');
        if (orderItemsQty === (orderItemDetailsQty + vehicleOrderDetailsQty)) {
            return true;
        }

        return false;
    }

    getNewOrder() {
        let order = {
            "id": 'new',
            "order_time": parseInt(moment().add(5, 'days').toDate().getTime(), 10),
            "property_type": undefined,
            "source_lift_availability": undefined,
            "destination_lift_availability": undefined,
            "time_slot": undefined,
            "move_type": undefined,
            "insurance": 0,
            "damage_cover": 0,
            "is_skipped": undefined,
            "is_express_delivery": undefined,
            "item_stats": [],
            "order_items": [],
            "from_address": {
                "flat_number": undefined,
                "floor_number": undefined,
                "appartment_name": undefined,
                "city": undefined,
                "landmark": undefined,
                "pincode": undefined,
                "name": '',
                "latitude": undefined,
                "longitude": undefined,
                "addressable_type": undefined,
                "addressable_id": undefined,
                "place_id": undefined
            },
            "to_address": {
                "flat_number": undefined,
                "floor_number": undefined,
                "appartment_name": undefined,
                "city": undefined,
                "landmark": undefined,
                "pincode": undefined,
                "name": '',
                "latitude": undefined,
                "longitude": undefined,
                "addressable_type": undefined,
                "addressable_id": undefined,
                "place_id": undefined
            }
        };

        return this.$q.when(this.rubifyOrder(order));
    }

    rubifyOrder(order) {
        // Ruby conventions demand this be in this format.
        order.order_items = _.map(order.order_items, (item) => {
            item.order_item_details = _.indexBy(item.order_item_details, (item, index) => index);
            return item;
        });
        order.order_items = _.indexBy(order.order_items, (item, index) => index);
        order.order_time = parseInt(moment(order.order_time).toDate().getTime() / 1000, 10);

        return order;
    }

    constants = {
        UNSUPPORTED_ORDER_VERSION: UNSUPPORTED_ORDER_VERSION,
        NO_QUOTES_FOR_ORDER: NO_QUOTES_FOR_ORDER,
        FINALIZED_ORDER_EDIT: FINALIZED_ORDER_EDIT,
        GO_FIGURE: GO_FIGURE,
        EXPIRED_ORDER: EXPIRED_ORDER,
        ORDER_NOT_QUOTED: ORDER_NOT_QUOTED,
        NO_ORDER_FOUND: NO_ORDER_FOUND,
        ORDER_IN_DRAFTED: ORDER_IN_DRAFTED,
        ORDER_IN_QUOTED: ORDER_IN_QUOTED,
        ORDER_IN_CONFIRMED: ORDER_IN_CONFIRMED,
        NO_BILL_FOUND: NO_BILL_FOUND,
        MAX_CUBIC_FEET: 1000
    };

    timeSlots = [
        {displayName: '8am - 10am', value: 'time_slot_1'},
        {displayName: '10am - 12pm', value: 'time_slot_2'},
        {displayName: '2pm - 4pm', value: 'time_slot_3'},
        {displayName: '4pm - 6pm', value: 'time_slot_4'}
    ]

    status = {
        DRAFTED: 'drafted',
        CREATED: 'created',
        QUOTED: 'quoted',
        CONFIRMED: 'confirmed',
        FINALIZED: 'finalized',
        ASSIGNED: 'assigned',
        CLOSED: 'closed',
        LOST: 'lost',
        INTRANSIT:  'in transit',
        INSTORAGE: 'in storage',
        DELIVERED: 'delivered',
        PICKUPCOMPLETED: 'pickup completed'
    }
}

export default OrderStore;


// WEBPACK FOOTER //
// ./client/app/common/stores/orderStore.js